/**
 * TranscriptContext - React Context for managing conversation transcript.
 *
 * Provides:
 * - Conversation transcript array
 * - Transcript loading state
 * - Actions for sending messages and loading transcript
 *
 * Part of the granular context split for performance optimisation.
 * Components that only need transcript won't re-render when
 * session metadata or images change.
 */

/* eslint-disable react-refresh/only-export-components -- Context exports hook alongside provider */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'
import type { ConversationTurn } from '@/models'
import { getApiClient, type SendMessageRequest, type SendMessageResponse } from '@/services/api'

/**
 * Transcript state
 */
export interface TranscriptState {
  transcript: ConversationTurn[]
  isLoading: boolean
  error: string | null
}

/**
 * Transcript actions
 */
export interface TranscriptActions {
  sendMessage: (sessionId: string, request: SendMessageRequest) => Promise<SendMessageResponse>
  loadTranscript: (sessionId: string) => Promise<ConversationTurn[]>
  clearTranscript: () => void
  setTranscript: (transcript: ConversationTurn[]) => void
}

/**
 * Transcript context value
 */
export interface TranscriptContextValue extends TranscriptState, TranscriptActions {}

const TranscriptContext = createContext<TranscriptContextValue | undefined>(undefined)

/**
 * Transcript provider props
 */
export interface TranscriptProviderProps {
  children: ReactNode
}

/**
 * Transcript provider component
 *
 * Manages only the conversation transcript and its loading/error states.
 * Separated from session data and images for granular re-renders.
 */
export function TranscriptProvider({ children }: TranscriptProviderProps) {
  const [state, setState] = useState<TranscriptState>({
    transcript: [],
    isLoading: false,
    error: null,
  })

  const apiClient = getApiClient()

  /**
   * Send conversation message
   */
  const sendMessage = useCallback(async (
    sessionId: string,
    request: SendMessageRequest
  ): Promise<SendMessageResponse> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await apiClient.sendMessage(sessionId, request)

      // Reload transcript to get the latest state
      const transcript = await apiClient.getTranscript(sessionId)

      setState(prev => ({
        ...prev,
        transcript,
        isLoading: false,
      }))

      return response
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send message'
      console.error('[TranscriptContext] sendMessage failed', {
        sessionId,
        error: error instanceof Error ? error.stack : error,
      })
      setState(prev => ({ ...prev, isLoading: false, error: message }))
      throw error
    }
  }, [apiClient])

  /**
   * Load transcript for a session
   */
  const loadTranscript = useCallback(async (sessionId: string): Promise<ConversationTurn[]> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const transcript = await apiClient.getTranscript(sessionId)
      setState({ transcript, isLoading: false, error: null })
      return transcript
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load transcript'
      console.error('[TranscriptContext] loadTranscript failed', {
        sessionId,
        error: error instanceof Error ? error.stack : error,
      })
      setState(prev => ({ ...prev, isLoading: false, error: message }))
      throw error
    }
  }, [apiClient])

  /**
   * Clear transcript state
   */
  const clearTranscript = useCallback(() => {
    setState({ transcript: [], isLoading: false, error: null })
  }, [])

  /**
   * Set transcript directly (for hydration/sync)
   */
  const setTranscript = useCallback((transcript: ConversationTurn[]) => {
    setState(prev => ({ ...prev, transcript }))
  }, [])

  const value = useMemo(() => ({
    ...state,
    sendMessage,
    loadTranscript,
    clearTranscript,
    setTranscript,
  }), [state, sendMessage, loadTranscript, clearTranscript, setTranscript])

  return (
    <TranscriptContext.Provider value={value}>
      {children}
    </TranscriptContext.Provider>
  )
}

/**
 * Hook to use transcript context
 */
export function useTranscript(): TranscriptContextValue {
  const context = useContext(TranscriptContext)

  if (!context) {
    throw new Error('useTranscript must be used within TranscriptProvider')
  }

  return context
}
